import * as React from "react"
import { useTranslation } from "react-i18next"

import { Enabled } from "Catalog/Edit/Deals/Content/Styles"
import Details from "Catalog/Edit/shared/components/Details"
import VariantPriceTooltip from "Catalog/Edit/shared/components/VariantPriceTooltip"
import useCatalogNavigation from "Catalog/shared/hooks/useCatalogNavigation"
import StatefulField from "shared/components/StatefulField"
import useEditor from "shared/hooks/useEditor"
import useHandleImageUpload from "shared/hooks/useHandleImageUpload"
import useNavigateNoUpdate from "shared/hooks/useNavigateNoUpdate"
import { CategoryId } from "shared/hubrise/ids"
import { IDeal } from "shared/hubrise/models"

import Commands from "./Commands"
import Lines from "./Lines"
import LinesSkus from "./LinesSkus"

const Content = ({ deal }: { deal: IDeal }): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigateNoUpdate()

  const { editor, catalog } = useEditor()
  const { catalogNavigation } = useCatalogNavigation()
  const handleImageUpload = useHandleImageUpload()

  const handleChange =
    <TField extends keyof IDeal>(field: TField, options?: { collapse?: boolean }) =>
    (value: IDeal[TField]) =>
      editor.deal.update(deal.id, field, value, options)

  const options = catalog.data.categories.map((category) => ({ label: category.name, value: String(category.id) }))
  const emptyOption = { label: t("deal.category.none"), value: "" }

  return (
    <>
      <Details.Topbar commands={<Commands deal={deal} />} onClose={() => navigate(catalogNavigation.editPath("deals"))}>
        <StatefulField.Title
          name="deal-name"
          initialValue={deal.name}
          placeholder={t("deal.name.placeholder")}
          onChange={handleChange("name")}
          validate={(name) => (name.length === 0 ? t("validations.not_empty") : undefined)}
        />
      </Details.Topbar>

      <StatefulField.Input
        name="deal-ref"
        initialValue={deal.ref}
        label={t("deal.ref.label")}
        withMargin
        onChange={handleChange("ref")}
      />

      <StatefulField.TextEditor
        name="deal-description"
        initialValue={deal.description}
        label={t("deal.description.label")}
        withMargin
        placeholder={t("deal.description.placeholder")}
        onChange={handleChange("description")}
      />

      <StatefulField.ImagesHolder
        name="deal-images"
        initialValue={deal.imageHrIds}
        label={t("deal.images")}
        withMargin
        onChange={handleChange("imageHrIds", { collapse: false })}
        onUpload={handleImageUpload}
        imagePathResolver={(imageHrId) => catalogNavigation.imagePath(imageHrId)}
        modalTitle={deal.name}
      />

      <Enabled>
        <StatefulField.Checkbox
          name="deal-enabled"
          initialValue={deal.restrictions.enabled}
          text={t("deal.enabled.text")}
          onChange={(enabled) => editor.deal.enable(deal.id, enabled)}
        />

        <VariantPriceTooltip
          catalog={catalog}
          navigateToGrid={() => navigate(catalogNavigation.gridPath("deal", deal.id))}
          restrictions={deal.restrictions}
        />
      </Enabled>

      <StatefulField.Select
        name="deal-category"
        initialValue={[String(deal.categoryId ?? "")]}
        label={t("deal.category.label")}
        withMargin
        onChange={(categoryIds) => {
          const categoryId = categoryIds[0]
          editor.deal.update(deal.id, "categoryId", categoryId ? CategoryId(Number(categoryId)) : null)
        }}
        options={[emptyOption, ...options]}
        searchable={true}
      />

      <Lines deal={deal} />

      <LinesSkus deal={deal} />
    </>
  )
}

export default React.memo(Content)
